import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Loader2, Sparkles, ArrowRight, CheckCircle2, Scale,
  Shield, Gavel, Building2, FileText, AlertTriangle,
  ChevronRight, Briefcase, Target, Zap
} from "lucide-react";
import { API } from "@/constants";

const CASE_TYPES = [
  { value: "civil", label: "Civil", icon: Scale, color: "bg-blue-50 border-blue-200 text-blue-700 data-[active=true]:bg-blue-600 data-[active=true]:text-white data-[active=true]:border-blue-600" },
  { value: "criminal", label: "Criminal", icon: Gavel, color: "bg-red-50 border-red-200 text-red-700 data-[active=true]:bg-red-600 data-[active=true]:text-white data-[active=true]:border-red-600" },
  { value: "constitutional", label: "Constitutional", icon: FileText, color: "bg-purple-50 border-purple-200 text-purple-700 data-[active=true]:bg-purple-600 data-[active=true]:text-white data-[active=true]:border-purple-600" },
  { value: "small_claims", label: "Small Claims", icon: Building2, color: "bg-amber-50 border-amber-200 text-amber-700 data-[active=true]:bg-amber-600 data-[active=true]:text-white data-[active=true]:border-amber-600" },
  { value: "government", label: "Government", icon: Shield, color: "bg-emerald-50 border-emerald-200 text-emerald-700 data-[active=true]:bg-emerald-600 data-[active=true]:text-white data-[active=true]:border-emerald-600" },
];

const DEGREES = [
  { value: "reasonable", label: "Reasonable", desc: "Standard complexity, straightforward facts", color: "border-green-300 data-[active=true]:bg-green-600 data-[active=true]:text-white data-[active=true]:border-green-600" },
  { value: "professional", label: "Professional", desc: "Moderate complexity, requires expertise", color: "border-blue-300 data-[active=true]:bg-blue-600 data-[active=true]:text-white data-[active=true]:border-blue-600" },
  { value: "difficult", label: "Difficult", desc: "High complexity, significant legal challenges", color: "border-red-300 data-[active=true]:bg-red-600 data-[active=true]:text-white data-[active=true]:border-red-600" },
];

const SIDES = [
  { value: "plaintiff", label: "Plaintiff", desc: "Initiating the claim" },
  { value: "defense", label: "Defense", desc: "Defending against the claim" },
];

const CaseStarterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    case_name: "",
    scenario: "",
    case_types: [],
    degree: "",
    side: "",
  });

  const toggleCaseType = (val) => {
    setForm((prev) => ({
      ...prev,
      case_types: prev.case_types.includes(val)
        ? prev.case_types.filter((t) => t !== val)
        : [...prev.case_types, val],
    }));
  };

  const isValid = form.case_name.trim() && form.scenario.trim() && form.case_types.length > 0 && form.degree && form.side;

  const handleSubmit = async () => {
    if (!isValid) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/wizard/case/quick-start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setResult(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const scoreColor = (score) => {
    if (score >= 70) return "text-emerald-600";
    if (score >= 40) return "text-amber-600";
    return "text-red-600";
  };

  const riskBadge = (level) => {
    const map = { low: "bg-green-100 text-green-700", medium: "bg-amber-100 text-amber-700", high: "bg-red-100 text-red-700" };
    return map[level] || map.medium;
  };

  if (result) {
    const ai = result.ai_analysis || {};
    return (
      <div className="max-w-3xl mx-auto py-8 px-4 space-y-6" data-testid="case-starter-result">
        {/* Header */}
        <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #1E40AF, #1E3A8A)" }}>
          <div className="flex items-center gap-3 mb-1">
            <Sparkles className="h-6 w-6" />
            <h1 className="text-xl font-bold">Case Analysis Complete</h1>
          </div>
          <p className="text-blue-200 text-sm">{form.case_name}</p>
        </div>

        {/* Score + Risk */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 pb-5 text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Strength Score</p>
              <p className={`text-4xl font-bold ${scoreColor(ai.strength_score)}`} data-testid="strength-score">
                {ai.strength_score}<span className="text-lg text-slate-400">/100</span>
              </p>
              <Progress value={ai.strength_score} className="mt-3 h-2" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 pb-5 text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Risk Level</p>
              <Badge className={`text-sm px-4 py-1.5 ${riskBadge(ai.risk_level)}`} data-testid="risk-level">
                {(ai.risk_level || "").toUpperCase()}
              </Badge>
              <p className="text-xs text-slate-400 mt-2">Complexity: {ai.estimated_complexity}</p>
            </CardContent>
          </Card>
        </div>

        {/* Summary */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5">
            <p className="text-sm font-semibold text-slate-700 mb-2">Case Summary</p>
            <p className="text-slate-600 text-sm leading-relaxed" data-testid="case-summary">{ai.summary}</p>
          </CardContent>
        </Card>

        {/* Key Considerations */}
        {ai.key_considerations?.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Target className="h-4 w-4 text-blue-600" />
                Key Considerations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {ai.key_considerations.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm">
                  <ChevronRight className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Next Steps */}
        {ai.recommended_next_steps?.length > 0 && (
          <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                Recommended Next Steps
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {ai.recommended_next_steps.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Challenges */}
        {ai.potential_challenges?.length > 0 && (
          <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                Potential Challenges
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {ai.potential_challenges.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button onClick={() => navigate(`/case/${result.case_id}`)} className="flex-1 h-11 bg-blue-600 hover:bg-blue-700" data-testid="view-case-btn">
            View Full Case <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => navigate(`/tools/litix-wizard`)} className="flex-1 h-11" data-testid="full-wizard-btn">
            Open Full Wizard
          </Button>
          <Button variant="ghost" onClick={() => { setResult(null); setForm({ case_name: "", scenario: "", case_types: [], degree: "", side: "" }); }} data-testid="new-case-btn">
            New Case
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 space-y-6" data-testid="case-starter-form">
      {/* Header */}
      <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #1E40AF, #1E3A8A)" }}>
        <div className="flex items-center gap-3 mb-1">
          <Zap className="h-6 w-6" />
          <h1 className="text-xl font-bold">Start New Case</h1>
        </div>
        <p className="text-blue-200 text-sm">Quick case setup with AI-powered analysis</p>
      </div>

      {/* Form */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6 space-y-6">
          {/* Case Name */}
          <div>
            <Label className="text-sm font-semibold">Case Name <span className="text-red-500">*</span></Label>
            <Input
              placeholder="e.g. Smith v. Jones, State v. Williams"
              value={form.case_name}
              onChange={(e) => setForm({ ...form, case_name: e.target.value })}
              className="mt-1.5 h-11"
              data-testid="case-name-input"
            />
          </div>

          {/* Scenario */}
          <div>
            <Label className="text-sm font-semibold">Describe the Scenario <span className="text-red-500">*</span></Label>
            <Textarea
              placeholder="Describe the facts, parties involved, and what happened..."
              value={form.scenario}
              onChange={(e) => setForm({ ...form, scenario: e.target.value })}
              className="mt-1.5 min-h-[120px]"
              data-testid="scenario-input"
            />
          </div>

          {/* Case Type - Multi select */}
          <div>
            <Label className="text-sm font-semibold">Type of Case <span className="text-red-500">*</span></Label>
            <p className="text-xs text-slate-500 mb-2">Select one or more</p>
            <div className="flex flex-wrap gap-2">
              {CASE_TYPES.map((ct) => {
                const active = form.case_types.includes(ct.value);
                const Icon = ct.icon;
                return (
                  <button
                    key={ct.value}
                    data-active={active}
                    onClick={() => toggleCaseType(ct.value)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${ct.color}`}
                    data-testid={`case-type-${ct.value}`}
                  >
                    <Icon className="h-4 w-4" />
                    {ct.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Degree */}
          <div>
            <Label className="text-sm font-semibold">Degree / Position <span className="text-red-500">*</span></Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              {DEGREES.map((d) => {
                const active = form.degree === d.value;
                return (
                  <button
                    key={d.value}
                    data-active={active}
                    onClick={() => setForm({ ...form, degree: d.value })}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${d.color} ${active ? "" : "bg-white hover:bg-slate-50"}`}
                    data-testid={`degree-${d.value}`}
                  >
                    <p className="text-sm font-semibold">{d.label}</p>
                    <p className={`text-[10px] mt-0.5 ${active ? "text-white/80" : "text-slate-500"}`}>{d.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Side */}
          <div>
            <Label className="text-sm font-semibold">Side <span className="text-red-500">*</span></Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {SIDES.map((s) => {
                const active = form.side === s.value;
                return (
                  <button
                    key={s.value}
                    onClick={() => setForm({ ...form, side: s.value })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      active
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white border-slate-200 hover:border-blue-300"
                    }`}
                    data-testid={`side-${s.value}`}
                  >
                    <p className="text-sm font-bold">{s.label}</p>
                    <p className={`text-xs mt-0.5 ${active ? "text-blue-100" : "text-slate-500"}`}>{s.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Submit */}
      <Button
        onClick={handleSubmit}
        disabled={loading || !isValid}
        className="w-full h-12 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40"
        data-testid="submit-case-starter"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Analyzing Case...
          </>
        ) : (
          <>
            <Sparkles className="mr-2 h-5 w-5" />
            Create Case & Generate Analysis
          </>
        )}
      </Button>
    </div>
  );
};

export default CaseStarterPage;
